import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ActivityIndicator, FlatList, Image, Platform } from 'react-native'

import { useRoute } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import { zetProList } from '@zet/common/utils/zetProData'
import { useFetchCustomerDetails } from '@zet/customer/src/api/hooks/useFetchCustomerDetails'
import {
  BottomSheet,
  Icon,
  Pressable,
  SH3,
  Stack,
  XStack,
  colors,
} from '@zet/design-system'
import { APP_NAME } from '@zet/end-points'
import { useTier } from '@zet/feature-loyalty/api/hooks'
import { useUserContext } from '@zet/feature-user'
import { IntegrationFlowHeader, M14 } from '@zet/integration-flow'
import useNativeAppBridge from '@zet/integration-flow/hooks/useNativeAppBridge'
import { EditCustomerBasicDetails } from '@zet/integration-flow/screens/editBasicCustomerDetails'
import { sendAnalyticsData } from '@zet/integration-flow/utils/analyticsEvents'
import { i18n } from '@zet/localization'
import { Categories } from '@zet/utils/Constants'
import { IBrandListRoute } from '@zet/utils/interface'
import useGetAgentDetailsAndTheme from '@zet/zet-pro-quick-journey/hooks/useGetAgentDetailsAndTheme'
import { useGetExperimentForZetBanner } from '@zet/zet-referral/src/api/hooks'
import { ZetBanner } from '@zet/zet-referral/src/components/zetBanner'
import { useUnsafeVariable } from 'nativewind'

import {
  useGenerateSharableLink,
  useGetLoyaltyTierDetails,
} from '../../api/brands/hooks'
import LeftArrowIcon from '../../assets/svg/LeftArrow.svg'
import BottomOtherCategories from '../../components/bottomOtherCategories'
import BrandCardV2 from '../../components/brandCardV2'
import CategoryNotEligible from '../../components/categoryNotEligible'
import NoOfferV2 from '../../components/noOfferV2'
import SbmCrossSell from '../../components/sbmCrossSell'
import SbmInfoCard from '../../components/sbmInfoCard'
import { useApplyLink } from '../../hooks/useApplyLink'
import {
  IProduct,
  useBrandRecommendations,
  useGetPreApprovedLoans,
} from '../../hooks/useBrandRecommendations'
import useFilterStore from '../../hooks/useFilterStore'
import useRecommendationsFlatlistRef from '../../hooks/useRecommendationsFlatlistRef'
import useVariantListingStore from '../../hooks/useVariantListingStore'
import useBrandStore from '../../store/brands'
import {
  RECOMMENDATION_API_FAILED,
  RECOMMENDATION_API_SUCCESS,
  RECOMMENDATION_WEB_PAGE_LOADED,
} from '../../utils/constants'
import OfferAndLoyaltyCoins from './OfferAndLoyaltyCoins'
import BrandListHeaderV2 from './brandListHeaderV2'
import CardTitle from './cardTitle'
import CbmCrossSell from './cbmCrossSell'
import FilterContainer from './filterContainer'
import MagnetCrossSell from './magnetCrossSell'
import ProductInEligible from './productInEligible'
import ProductSeparator from './productSeparator'
import ScreenLoader from './screenLoader'
import SeeMoreFooter from './seeMoreFooter'

const isWeb = Platform.OS === 'web'

const PERSONAL_LOAN_CATEGORY_ID = 9
const PRE_APPROVED_CROSS_SELL_CATEGORY_ID = 18

const BrandListV2 = () => {
  const route = useRoute()
  const params = route.params as IBrandListRoute

  const customerId = params?.customerId
  const isBureauReportExpired = params?.isBureauReportExpired
  const isRecommendationFlow = isWeb || !!params?.customerId
  const brandSpecificPartnerId = params?.brandSpecificPartnerId
  const partnerId = params?.partnerId

  const [viewMoreCount, setViewMoreCount] = useState(0)
  const [seeMoreFooterBMS, setSeeMoreFooterBMS] = useState(true)
  const [topProductsArray, setTopProductsArray] = useState<any>([])
  const [showEditBasicDetailsSheet, setShowEditBasicDetailsSheet] =
    useState(false)

  const [seeMoreFooter, setSeeMoreFooter] = useState(true)
  const [preApprovedLoansArray, setPreApprovedLoansArray] = useState([])

  const recommendationsFlatlistRef = useRecommendationsFlatlistRef()
  const [colorPrimary100] = useUnsafeVariable('--color-primary-100')
  const hasSentAnalytics = useRef(false)

  const {
    isBMSFlow,
    isJfsFlow,
    nonTopProductTitle,
    topProductTitle,
    agentData: agentDetailsData,
  } = useGetAgentDetailsAndTheme()

  const { changeHeaderVisibility } = useNativeAppBridge()

  const selectedFiltersTransformed = useFilterStore(
    (state) => state?.selectedFiltersTransformed
  )

  const currentCategoryId =
    selectedFiltersTransformed?.categoryId || params?.categoryId

  const variantId = params?.variantId || agentDetailsData?.variantId

  const {
    refetch,
    data: listings,
    fetchNextPage,
    hasNextPage,
    isLoading: isListingLoading,
    isFetchingNextPage,
    isInitialLoading,
    isError,
  } = useBrandRecommendations({
    categoryId: currentCategoryId,
    isDemoSale: params?.isDemoSale,
    variantId,
    customerId,
  })

  const isFromCustomerAddition =
    currentCategoryId === 9 && !!params?.customerName

  const { data: preApprovedLoans, isFetched: isPreApprovedLoansFetched } =
    useGetPreApprovedLoans({
      categoryId: params?.categoryId,
      customerId,
      variantId,
      enabled: isFromCustomerAddition,
    })

  const preApprovedProducts = preApprovedLoans?.products || []

  const totalPreApprovedLoans = preApprovedProducts?.length || 0

  const isPreApprovedLoansAvailable = totalPreApprovedLoans > 0

  const preApprovedCategoryId = preApprovedLoans?.categoryId

  const { data: currentTier } = useTier()
  const { data: alLAchivedTiersStatus } = useGetLoyaltyTierDetails()

  const { setUserPincode } = useBrandStore((state) => state)

  const { showPayout, subFilterRef } = useVariantListingStore((state) => ({
    showPayout: state?.showPayout,
    subFilterRef: state?.subFilterRef,
  }))

  const {
    filterSelectedCategoryId,
    clearAllFilters,
    setCategoryId,
    setFiltersFromParam,
  } = useFilterStore((state) => ({
    filterSelectedCategoryId: state?.categoryId,
    setCategoryId: state?.setCategoryId,
    clearAllFilters: state?.clearAllFilters,
    setFiltersFromParam: state?.setFiltersFromParam,
  }))

  const currentListings =
    listings?.pages?.[(listings?.pages?.length ?? 1) - 1]?.data

  const categoryId: string | number =
    filterSelectedCategoryId ||
    params?.categoryId ||
    currentListings?.categoryId

  const {
    categoryName,
    product,
    customerLeads,
    isCategoryInEligible,
    totalProductCount = 0,
    products: allProducts,
    topProducts = [],
    paginationMeta,
    otherCategoryProducts,
    eligibleCardsCountExcludingMagnet,
  } = currentListings || {}

  const products: Array<IProduct | string> | undefined = allProducts
  const [isRedirectionLoading, setIsRedirectionLoading] = useState(
    product?.eligibility?.isEligible
  )

  const inEligibleProductStartingIndex =
    products?.findIndex(
      (element: IProduct | string) => element?.eligibility?.isEligible === false
    ) ?? -1
  if (inEligibleProductStartingIndex >= 0) {
    products?.splice(inEligibleProductStartingIndex, 0, 'OTHER_OFFERS')
  }

  // Get details of a customer based on the customer ID
  const { data: customerData } = useFetchCustomerDetails(customerId)
  const { data: userContext } = useUserContext(
    APP_NAME === 'zet-app' && Platform.OS === 'android'
  )
  const { data: experimentForZetBanner } = useGetExperimentForZetBanner()

  const customerName = customerData?.fullName
  const customerPhoneNumber = customerData?.phoneNumber?.toString()

  const configArrayBMS = useMemo(() => ['FILTERS', 'OFFER_HEADER'], [])
  const configArray = ['FILTERS']

  const ifEmpty = Boolean(isCategoryInEligible || !products?.length)

  const isBMSEmpty = Boolean(
    isCategoryInEligible || (!products?.length && !topProducts?.length)
  )

  const isZetPro = zetProList.includes(String(agentDetailsData?.onecodeId))

  const sbmData = products?.find((item) => item?.categoryId === 18)

  const PreApprovedrecommendations = useMemo(() => {
    let preApprovedArray: Array<string | IProduct> = []
    if (preApprovedCategoryId === PERSONAL_LOAN_CATEGORY_ID) {
      preApprovedLoansArray?.length &&
        preApprovedArray.push(
          ...['PRE_APPROVED_OFFER_HEADER', ...preApprovedLoansArray]
        )

      preApprovedArray =
        isPreApprovedLoansAvailable && totalPreApprovedLoans > 2
          ? [...preApprovedArray, 'SHOW_SEE_MORE']
          : preApprovedArray
    }

    const listingArray = []

    if (ifEmpty) {
      if (categoryId !== PERSONAL_LOAN_CATEGORY_ID) {
        listingArray.push('OFFER_HEADER', 'NO_OFFER')
      } else if (
        preApprovedCategoryId === PRE_APPROVED_CROSS_SELL_CATEGORY_ID
      ) {
        listingArray.push('OFFER_HEADER', 'NO_OFFER')
      }
    } else {
      if (products && products.length) {
        listingArray.push('OFFER_HEADER', ...products)
      }
    }
    let temp: Array<string | IProduct> = [...configArray]
    if (isJfsFlow && !!product && !product?.eligibility?.isEligible) {
      temp = [...configArray, 'PRODUCT_INELIGIBLE', ...listingArray]
    } else if (isWeb && isZetPro && categoryId === 18) {
      temp = [...configArray, 'SBM_INFO', ...listingArray]
    } else if (
      preApprovedLoans?.categoryId !== PERSONAL_LOAN_CATEGORY_ID &&
      ifEmpty
    ) {
      temp = [...configArray, 'NO_OFFER']
    } else {
      temp = [...configArray, ...preApprovedArray, ...listingArray]
    }
    return temp
  }, [
    preApprovedLoansArray,
    ifEmpty,
    products,
    preApprovedCategoryId,
    categoryId,
    isPreApprovedLoansAvailable,
  ])

  const BMSrecommendations = useMemo(() => {
    if (isBMSEmpty) {
      return [...(configArrayBMS || []), 'NO_OFFER']
    }
    if (Number(categoryId) === Categories.CREDIT_CARDS && isWeb) {
      if (topProducts?.length === 0 && isBMSEmpty) {
        return [...(configArrayBMS || []), 'NO_OFFER']
      }
      if (topProducts?.length > 0) {
        return [
          ...(configArrayBMS || []),
          ...(topProductTitle ? ['TOP_PRODUCT_TITLE'] : []),
          ...topProductsArray,
          ...(topProducts?.length > 3 ? ['SHOW_SEE_MORE_BMS'] : []),
          ...(products && products?.length > 0 && nonTopProductTitle
            ? ['NON_TOP_PRODUCT_TITLE']
            : []),
          ...(products || []),
        ]
      } else {
        return [
          ...(configArrayBMS || []),
          ...(products && products?.length > 0 && nonTopProductTitle
            ? ['NON_TOP_PRODUCT_TITLE']
            : []),
          ...(products || []),
        ]
      }
    }

    return [...(configArrayBMS || []), ...(products || [])]
  }, [
    topProducts,
    categoryId,
    topProductsArray,
    configArrayBMS,
    ifEmpty,
    products,
    nonTopProductTitle,
    topProductTitle,
  ])

  const recommendations = isBMSFlow
    ? BMSrecommendations
    : PreApprovedrecommendations

  useEffect(() => {
    if (
      Number(categoryId) === Categories.CREDIT_CARDS &&
      topProducts?.length > 0 &&
      isWeb
    ) {
      setTopProductsArray(
        !seeMoreFooterBMS ? topProducts : topProducts?.slice(0, 3)
      )
    }
  }, [categoryId, seeMoreFooterBMS, topProducts])

  const onSeeMorePress = () => {
    if (isBMSFlow) {
      setSeeMoreFooterBMS(!seeMoreFooterBMS)
    } else {
      setSeeMoreFooter(!seeMoreFooter)
    }
  }

  const refetchRecommendation = () => {
    refetch()
  }

  const totalofferCount = paginationMeta?.total || 0

  const handleOnNextPress = () => {
    if (hasNextPage) fetchNextPage()

    if (isWeb || customerId) {
      publishEvent('click_action', {
        item_name: `view_all_offers_${
          isWeb ? 'recommendation_web' : 'recommendation_app'
        }`,
      })
    }
  }

  useEffect(() => {
    changeHeaderVisibility(0)
  }, [])

  useEffect(() => {
    if (currentCategoryId === 9 && isPreApprovedLoansAvailable) {
      if (!seeMoreFooter) {
        setPreApprovedLoansArray([...preApprovedLoans?.products] || [])
      } else {
        setPreApprovedLoansArray(
          [...preApprovedLoans?.products?.slice(0, 2)] || []
        )
      }
    } else {
      setPreApprovedLoansArray([])
    }
  }, [seeMoreFooter, isPreApprovedLoansFetched])

  useEffect(() => {
    if (isWeb) {
      window?.ReactNativeWebView?.postMessage(RECOMMENDATION_WEB_PAGE_LOADED)
    }
  }, [])

  useEffect(() => {
    if (isWeb && !isInitialLoading) {
      window?.ReactNativeWebView?.postMessage(
        isError ? RECOMMENDATION_API_FAILED : RECOMMENDATION_API_SUCCESS
      )
    }
  }, [isError, isInitialLoading])

  useEffect(() => {
    if (Number(products?.length) > 0) {
      let visibleRemainingCount = 0

      products?.forEach((product) => {
        visibleRemainingCount +=
          1 + (product?.remainingCount ? product?.remainingCount : 0)
      })

      const _viewMoreCount = totalProductCount - visibleRemainingCount

      if (_viewMoreCount > 0) {
        setViewMoreCount(_viewMoreCount)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])

  //! This effect is specific to reset the pincode if category changes and publish the event
  useLayoutEffect(() => {
    if (!customerId) {
      setUserPincode('')
    }
    return () => {
      !isRecommendationFlow && setUserPincode('')
    }
  }, [customerId, isRecommendationFlow, setUserPincode])

  useEffect(() => {
    return () => {
      clearAllFilters({
        maintainCategoryId: false,
        resetInitialCategory: true,
      })
    }
  }, [clearAllFilters])

  useEffect(() => {
    if (categoryId) {
      publishEvent('screen_view', {
        screen_name: `new_${
          isWeb
            ? brandSpecificPartnerId
              ? 'recommendation_web_brand_specific'
              : 'recommendation_web_open_link'
            : params?.customerId
            ? 'recommendation_app'
            : 'brand_page'
        }`,
        product: categoryId,
      })
    }
  }, [categoryId, brandSpecificPartnerId, params?.customerId])

  useEffect(() => {
    if (
      !hasSentAnalytics.current &&
      isJfsFlow &&
      !!product &&
      !product?.eligibility?.isEligible
    ) {
      hasSentAnalytics.current = true
      sendAnalyticsData({
        screen: 'SHOW_QUICK_RECOMMENDATION_SCREEN',
        type: 'screen',
        optional_data: {
          type: 'product_ineligible',
          productId: params?.variantId,
        },
      })
    }
  }, [product, isJfsFlow, params?.variantId])

  // Sets the initial Params to the Filter Store
  useEffect(() => {
    setFiltersFromParam(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFiltersFromParam])

  const {
    mutate: fetchShareLink,
    isLoading,
    isSuccess,
    data: shareLinkData,
  } = useGenerateSharableLink()

  const { handleApply } = useApplyLink(
    shareLinkData,
    partnerId,
    params?.customerName || customerName,
    customerId,
    customerPhoneNumber,
    false,
    null,
    null,
    isJfsFlow,
    false
  )

  useEffect(() => {
    const triggerFlow = async () => {
      if (
        isJfsFlow &&
        customerId &&
        variantId &&
        product?.eligibility?.isEligible
      ) {
        setIsRedirectionLoading(true)
        try {
          const startTime = Date.now()

          await fetchShareLink({
            partnerId: product?.partnerId,
            customerId,
            leadSource: 'web',
            brandSpecificPartnerId: params?.brandSpecificPartnerId,
            variantId,
          })

          if (isSuccess && shareLinkData) {
            await Promise.resolve(handleApply())
          }

          // Wait to ensure loader shows for at least 2 seconds
          const elapsed = Date.now() - startTime
          const delay = Math.max(3000 - elapsed, 0)

          setTimeout(() => {
            setIsRedirectionLoading(false)
          }, delay)
        } catch (error) {
          console.error('Error in JFS flow:', error)
          setIsRedirectionLoading(false)
        }
      }
    }

    triggerFlow()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJfsFlow, customerId, variantId, product, isSuccess, shareLinkData])

  if (
    (isRedirectionLoading || isListingLoading || isLoading) &&
    (!products?.length || isRedirectionLoading || isLoading)
  ) {
    return (
      <>
        <Header
          categoryName={categoryName}
          categoryId={categoryId}
          customerId={customerId}
          customerName={customerName}
          customerPhoneNumber={customerPhoneNumber}
          isBureauReportExpired={isBureauReportExpired}
          subAgentId={customerData?.subAgentId}
        />
        <ScreenLoader showLottie isJFSFlow={isJfsFlow} />
      </>
    )
  }
  return (
    <Stack className="flex-1 bg-white">
      <FlatList
        ref={recommendationsFlatlistRef}
        data={recommendations}
        stickyHeaderIndices={[1]}
        contentContainerStyle={{ flexGrow: 1 }}
        keyExtractor={(_, index) => index.toString()}
        renderItem={({ item }) => {
          if (item === 'OTHER_OFFERS') {
            return categoryId !== 18 ? <ProductSeparator /> : <></>
          }
          if (item === 'FILTERS') {
            return (
              <FilterContainer
                key={item}
                isCategoryInEligible={isCategoryInEligible}
                otherCategories={otherCategoryProducts}
                categoryId={categoryId}
                isRecommendationFlow={Boolean(isRecommendationFlow)}
                customerId={customerId}
                isBMSFlow={isBMSFlow}
                isJfsFlow={isJfsFlow}
                selectedCategory={{
                  categoryId,
                  categoryName,
                  totalCount: totalofferCount,
                }}
              />
            )
          }
          if (item === 'OFFER_HEADER') {
            return (
              <>
                <OfferAndLoyaltyCoins
                  key={item}
                  categoryName={categoryName}
                  headerText={
                    Number(categoryId) === PERSONAL_LOAN_CATEGORY_ID &&
                    isFromCustomerAddition
                      ? i18n.t('otherOffers')
                      : ''
                  }
                  customerId={Number(customerId)}
                  currentTierName={
                    Number(categoryId) === PERSONAL_LOAN_CATEGORY_ID
                      ? ''
                      : currentTier?.cohort
                  }
                  customerLeads={
                    !preApprovedLoans?.products?.length &&
                    !!params?.customerName
                      ? customerLeads
                      : []
                  }
                  customerPhoneNumber={customerPhoneNumber}
                  totalOffers={totalProductCount}
                  categoryId={categoryId?.toString()}
                  routeName={params?.routeName}
                />
                {/* {params?.categoryId === 19 && (
                  <MagnetBanner
                    isNewFlow={true}
                    totalOffers={totalProductCount}
                  />
                )} */}
              </>
            )
          }
          if (item === 'PRE_APPROVED_OFFER_HEADER') {
            return (
              <OfferAndLoyaltyCoins
                key={item}
                categoryName={preApprovedLoans?.categoryName}
                prequalifiedOffers={true}
                customerId={Number(customerId)}
                customerLeads={customerLeads}
                customerPhoneNumber={customerPhoneNumber}
                totalOffers={preApprovedLoans?.products?.length}
                categoryId={categoryId?.toString()}
              />
            )
          }
          if (
            item === 'NO_OFFER' &&
            preApprovedCategoryId === PRE_APPROVED_CROSS_SELL_CATEGORY_ID &&
            isPreApprovedLoansAvailable
          ) {
            return <MagnetCrossSell item={preApprovedProducts?.[0]} />
          }

          if (item === 'SHOW_SEE_MORE_BMS') {
            return (
              <SeeMoreFooter
                seeMore={seeMoreFooterBMS}
                onPress={onSeeMorePress}
                isBMSFlow={isBMSFlow}
              />
            )
          }
          if (item === 'SHOW_SEE_MORE') {
            return (
              <SeeMoreFooter
                seeMore={seeMoreFooter}
                count={totalPreApprovedLoans - 2}
                onPress={onSeeMorePress}
                isBMSFlow={false}
              />
            )
          }
          if (item === 'TOP_PRODUCT_TITLE') {
            return <CardTitle title={topProductTitle!} />
          }
          if (item === 'NON_TOP_PRODUCT_TITLE') {
            return <CardTitle title={nonTopProductTitle!} />
          }
          if (item === 'NO_OFFER' && !isListingLoading) {
            return (
              <>
                {isCategoryInEligible ? (
                  <CategoryNotEligible key={item} categoryName={categoryName} />
                ) : (
                  <NoOfferV2
                    categoryId={categoryId}
                    customerId={customerId}
                    key={item}
                    categoryName={categoryName || 'product'}
                    onClearFilter={() => {
                      !isRecommendationFlow && setUserPincode('')
                      clearAllFilters()
                      subFilterRef?.current?.resetLocalState()
                    }}
                    isBMSFlow={isBMSFlow}
                    isJfsFlow={isJfsFlow}
                  />
                )}
                {!isWeb ? (
                  <>
                    <SbmCrossSell
                      sectionTitle={'Customer eligible for FD Credit Card'}
                    />
                    <CbmCrossSell sectionTitle={'Customer eligible for CBM'} />
                  </>
                ) : null}
              </>
            )
          }
          if (item === 'PRODUCT_INELIGIBLE') {
            const { quickFilter, sort, groupCode } =
              selectedFiltersTransformed || {}
            if (
              quickFilter?.length > 0 ||
              Number(sort?.length) > 0 ||
              groupCode
            ) {
              return null
            } else {
              return (
                <ProductInEligible
                  product={product}
                  otherProduct={products?.length}
                />
              )
            }
          }
          if (item === 'SBM_INFO') {
            return (
              <Stack className="my-4/2 mx-3">
                <SbmInfoCard
                  customerId={customerId}
                  customerName={customerName}
                  customerPhoneNumber={customerPhoneNumber}
                  data={sbmData}
                  eligibleCardsCountExcludingMagnet={
                    eligibleCardsCountExcludingMagnet
                  }
                  isSourceWeb={true}
                />
              </Stack>
            )
          }
          return (
            <Stack className="my-4/2 mx-3">
              <BrandCardV2
                key={item?.id}
                data={item}
                showPayout={showPayout}
                categoryId={categoryId}
                tierData={alLAchivedTiersStatus}
                currentTier={currentTier}
                eligibleCardsCountExcludingMagnet={
                  eligibleCardsCountExcludingMagnet
                }
                isJfsFlow={isJfsFlow}
              />
            </Stack>
          )
        }}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={
          <>
            {isWeb && isJfsFlow ? (
              <IntegrationFlowHeader
                setShowEditBasicDetailsSheet={setShowEditBasicDetailsSheet}
              />
            ) : null}
            <Header
              categoryName={categoryName}
              categoryId={categoryId}
              customerId={customerId}
              customerName={customerName}
              customerPhoneNumber={customerPhoneNumber}
              isBureauReportExpired={isBureauReportExpired}
              subAgentId={customerData?.subAgentId}
            />
            {isWeb && !isJfsFlow && !!product && (
              <Stack className="bg-primary-200 px-3 py-4">
                <BrandCardV2
                  isSourceWeb={true}
                  isHeroCard
                  data={product}
                  showPayout={showPayout}
                  categoryId={categoryId}
                  tierData={alLAchivedTiersStatus}
                  currentTier={currentTier}
                  eligibleCardsCountExcludingMagnet={
                    eligibleCardsCountExcludingMagnet
                  }
                />
              </Stack>
            )}
            {!isWeb && isRecommendationFlow && (
              <Stack className="mt-2">
                <BottomOtherCategories
                  isCategoryInEligible={isCategoryInEligible}
                  otherCategories={otherCategoryProducts}
                  onPress={setCategoryId}
                />
                {experimentForZetBanner?.showZetReferralBanner && (
                  <Stack className="mt-3">
                    <ZetBanner
                      userId={userContext?.id}
                      screenName="recommendation"
                    />
                  </Stack>
                )}
              </Stack>
            )}
          </>
        }
        extraData={
          (JSON.stringify(recommendations) && seeMoreFooter) || seeMoreFooterBMS
        }
        ListFooterComponentStyle={{ flex: 1, justifyContent: 'flex-end' }}
        ListFooterComponent={
          <>
            {paginationMeta?.hasNextPage && (
              <>
                {isJfsFlow && viewMoreCount > 0 ? (
                  <Pressable
                    disabled={isFetchingNextPage}
                    onPress={handleOnNextPress}
                    className="m-3 h-[42px] items-center justify-center rounded-3xl bg-[#FDE7CB] px-3"
                  >
                    <XStack className="items-center">
                      <M14 className="text-[#CE912D]">
                        View {viewMoreCount} more offers
                      </M14>
                      {isFetchingNextPage ? (
                        <ActivityIndicator
                          size="small"
                          color={colors.primary[150]}
                          className="ml-2"
                        />
                      ) : (
                        <Stack className="ml-2">
                          <LeftArrowIcon />
                        </Stack>
                      )}
                    </XStack>
                  </Pressable>
                ) : null}

                {!isJfsFlow && viewMoreCount > 0 && (
                  <Pressable
                    disabled={isFetchingNextPage}
                    onPress={handleOnNextPress}
                  >
                    <XStack className="border-grays-40 mx-3 my-4 justify-between rounded-lg border bg-white p-3">
                      <SH3>
                        {i18n.t('brandPage.moreOffers', {
                          count: viewMoreCount,
                        })}
                      </SH3>
                      <XStack>
                        <SH3 className="text-primary-100">
                          {i18n.t('brandPage.viewAllOffers')}
                        </SH3>
                        {isFetchingNextPage ? (
                          <ActivityIndicator
                            size="small"
                            color={colors.primary[80]}
                            className="ml-2"
                          />
                        ) : (
                          <Icon type="ArrowRight" color={colorPrimary100} />
                        )}
                      </XStack>
                    </XStack>
                  </Pressable>
                )}
              </>
            )}

            {isWeb && (
              <Image
                source={require('../../assets/images/footer.png')}
                style={{
                  width: '100%',
                  aspectRatio: 2.8,
                  marginTop: 10,
                }}
                resizeMode="contain"
              />
            )}
          </>
        }
      />
      {isJfsFlow && (
        <BottomSheet
          snapPoints={[530]}
          visible={showEditBasicDetailsSheet}
          onDismiss={() => {
            setShowEditBasicDetailsSheet(false)
          }}
        >
          <EditCustomerBasicDetails
            refetchRecommendation={refetchRecommendation}
            setShowEditBasicDetailsSheet={setShowEditBasicDetailsSheet}
          />
        </BottomSheet>
      )}
    </Stack>
  )
}

const Header = React.memo(
  ({
    categoryName,
    categoryId,
    customerId,
    customerName,
    customerPhoneNumber,
    isBureauReportExpired,
    subAgentId,
  }: object) => (
    <BrandListHeaderV2
      categoryName={categoryName}
      categoryId={categoryId}
      customerId={customerId}
      customerName={customerName}
      customerPhoneNumber={customerPhoneNumber}
      isBureauReportExpired={isBureauReportExpired}
      subAgentId={subAgentId}
    />
  )
)

export default BrandListV2
